import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";

const SignupPage = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    adminName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.adminName || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await signup(
      formData.companyName,
      formData.email,
      formData.password,
      formData.adminName,
      formData.phone
    );
    setLoading(false);

    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/app');
    } else {
      toast.error(result.error);
    }
  };

  const features = [
    "Customer & Property Management",
    "Recurring Service Scheduling",
    "Route Planning & Optimization",
    "Mobile Crew App",
    "Invoicing & Payments",
    "Reporting & Analytics"
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <Link to="/" className="flex items-center mb-8">
              <img src={LOGO_URL} alt="Orchard LMS" className="h-10 w-auto" />
            </Link>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Start your free trial
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              14 days free. No credit card required.
            </p>
          </div>

          <div className="mt-8">
            <Card className="border-gray-200">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Your Landscaping Company"
                      className="mt-1"
                      data-testid="signup-company-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="adminName">Your Name *</Label>
                    <Input
                      id="adminName"
                      name="adminName"
                      type="text"
                      value={formData.adminName}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="mt-1"
                      data-testid="signup-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="mt-1"
                      data-testid="signup-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="mt-1"
                      data-testid="signup-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative mt-1">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        data-testid="signup-password-input"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="mt-1"
                      data-testid="signup-confirm-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={loading}
                    data-testid="signup-submit-btn"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>

                <p className="mt-4 text-xs text-gray-500 text-center">
                  By signing up, you agree to our{' '}
                  <a href="#" className="text-primary hover:text-primary/80">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-primary hover:text-primary/80">Privacy Policy</a>
                </p>
              </CardContent>
            </Card>

            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary hover:text-primary/80">
                Sign in
              </Link>
            </p>

            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Back to homepage
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Feature list */}
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-12">
        <div className="max-w-md">
          <h3 className="text-2xl font-bold text-white mb-8">
            Everything you need to run your landscaping business
          </h3>
          <ul className="space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-white">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Check className="h-4 w-4" />
                </div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <div className="mt-12 p-6 bg-white/10 rounded-xl">
            <p className="text-white/90 italic">
              "Orchard LMS helped us scale from 50 to 200 properties without adding office staff. 
              The route optimization alone saves us 10 hours per week."
            </p>
            <p className="mt-4 text-white font-medium">— Mike R., Green Valley Landscaping</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
